import Dexie, { type Table } from 'dexie'
import type { Book, Bookmark, Highlight, HighlightComment, ReadingSession, ReviewCard, Shelf, Summary, Translation } from '@/types/domain'
import type { BookIndexChunk } from '@/types/search'
import type { EmbeddingRow } from '@/types/embeddings'
import type { ChatThread } from '@/types/chat'
import type { ErrorLogEntry } from '@/db/error-log'
import type { QueuedEvent } from '@/composables/useEventTracker'
import type { AudioChunkRow } from '@/types/audio'

/**
 * Base de datos local de EPUB Unlimited (IndexedDB vía Dexie).
 *
 * Aquí vive TODO el estado de la app: metadatos de libros, los propios
 * archivos EPUB como Blobs, anotaciones, resúmenes y traducciones.
 * No hay backend: el navegador es el único dueño de los datos.
 *
 * Las migraciones de esquema se gestionan con `version().stores()`:
 * NUNCA borres ni renombres stores existentes; añade versiones nuevas.
 */
export class EpubUnlimitedDB extends Dexie {
  books!: Table<Book, string>
  highlights!: Table<Highlight, string>
  summaries!: Table<Summary, string>
  translations!: Table<Translation, string>
  bookmarks!: Table<Bookmark, string>
  /**
   * Tabla clave-valor para preferencias (tema, tipografía, config IA...).
   * Clave = string, valor = JSON serializado.
   */
  settings!: Table<{ key: string; value: unknown }, string>
  sessions!: Table<ReadingSession, string>
  errorLogs!: Table<ErrorLogEntry, string>
  /**
   * Estanterías personalizadas del usuario (grupos many-to-many de
   * libros). Sincronizadas vía CRDT en un doc global por usuario.
   */
  shelves!: Table<Shelf, string>
  /**
   * Índice de búsqueda: fragmentos de texto de los EPUBs y de las
   * anotaciones, para la búsqueda global. El índice FlexSearch en memoria se
   * reconstruye leyendo esta tabla al arrancar.
   */
  bookIndex!: Table<BookIndexChunk, string>
  /**
   * Embeddings (vectores semánticos) de chunks de libro y de highlights.
   * Se generan bajo demanda (conocimiento, Área C) y se persisten para no
   * recalcular. La PK coincide con el `chunkId`/`id` de la fuente.
   */
  embeddings!: Table<EmbeddingRow, string>
  /**
   * Hilos de chat RAG persistidos (Área D). Acotados a un libro
   * (`scope:'book'`, `bookId`) o a la biblioteca (`scope:'library'`).
   */
  chatThreads!: Table<ChatThread, string>
  /**
   * Tarjetas de repaso (SRS) generadas desde highlights. Sincronizadas vía
   * CRDT global por usuario. Índices por bookId (cascade/filtro), highlightId
   * (idempotencia), dueDate (vencidas) y color (filtro visual).
   */
  reviewCards!: Table<ReviewCard, string>
  /**
   * Cola de eventos pendientes de enviar al backend (tabla `app_events`).
   * Offline-first: los eventos se acumulan aquí y un flush periódico los
   * envía en batches. Si no hay sesión/Supabase, se quedan hasta haber.
   */
  eventQueue!: Table<QueuedEvent, string>
  /**
   * Comentarios sobre highlights en rooms compartidos (F2.1). Se
   * sincronizan vía CRDT en el mismo doc que los highlights. Índices por
   * highlightId (cascade/filtro) y [highlightId+createdAt] (orden hilo).
   */
  highlightComments!: Table<HighlightComment, string>
  /**
   * Binarios EPUB separados de la metadata del libro (v18). Hasta v17 el
   * `fileBlob` vivía inline en `books`; eso hacía que `getAllBooks()`
   * deserializara decenas de MB en cada apertura de la biblioteca. Ahora
   * se piden bajo demanda con `getBookFile(id)`.
   * Clave = bookId, valor = `{ id, blob }`.
   */
  bookFiles!: Table<{ id: string; blob: Blob }, string>
  /**
   * Portadas separadas de la metadata del libro (v18), igual que
   * `bookFiles`. Se piden bajo demanda con `getBookCover(id)`.
   */
  bookCovers!: Table<{ id: string; blob: Blob }, string>
  /**
   * Caché de audio sintetizado del modo audiolibro (v19). Clave =
   * `${chunkId}:${voice}`. Se desaloja por LRU contra el tope de Ajustes.
   * No se sincroniza: binario, enorme y con copyright.
   */
  audioCache!: Table<AudioChunkRow, string>
  /**
   * Favoritos cross-device (v20). Un registro por libro marcado como
   * favorito: `id` = bookId, `addedAt` = ISO timestamp. La fuente de
   * verdad es el CRDT `favorites:${userId}`; esta tabla es caché local.
   */
  favorites!: Table<{ id: string; addedAt: string }, string>

  constructor() {
    super('epub-unlimited')

    this.version(1).stores({
      // `&` = clave primaria única. Sin `&` en otros campos = índice.
      books: 'id, title, author, status, dateAdded, [status+dateAdded]',
      highlights: 'id, bookId, createdAt, [bookId+createdAt]',
      summaries: 'id, bookId, createdAt, [bookId+createdAt]',
      translations: 'id, bookId, createdAt, [bookId+createdAt]',
      settings: 'key',
    })

    // v2: sesiones de lectura para estadísticas.
    // Al declarar solo las tiendas nuevas/afectadas, Dexie preserva los
    // datos existentes. Las tiendas no mencionadas se heredan tal cual.
    this.version(2).stores({
      sessions: 'id, bookId, date, [bookId+date]',
    })

    // v3: marcadores (bookmarks) dentro de los libros.
    this.version(3).stores({
      bookmarks: 'id, bookId, createdAt, [bookId+createdAt]',
    })

    // v4: registro de errores local (observabilidad sin cuenta externa).
    this.version(4).stores({
      errorLogs: 'id, timestamp, level, seen',
    })

    // v5: índice contentHash en books para detectar duplicados al importar.
    // Se declara el store completo (no solo el nuevo índice) porque Dexie
    // reemplaza la definición del store en la versión nueva. Los datos
    // existentes se conservan; los libros antiguos tendrán contentHash
    // undefined hasta que se reimporten.
    this.version(5).stores({
      books: 'id, title, author, status, dateAdded, [status+dateAdded], contentHash',
    })

    // v6: estanterías personalizadas del usuario (grupos de libros).
    // Al declarar solo el store nuevo, Dexie preserva los datos existentes.
    this.version(6).stores({
      shelves: 'id, name, createdAt',
    })

    // v7: reservada (era catálogos OPDS, eliminados). Se mantiene el bump
    // de versión para no romper la secuencia de migraciones de Dexie.
    this.version(7).stores({})

    // v8: índice de búsqueda global. Chunks de texto de EPUBs y anotaciones.
    // Índice por bookId para borrado en cascada al eliminar un libro, y por
    // kind para distinguir contenido de libro vs. anotación.
    this.version(8).stores({
      bookIndex: 'id, bookId, kind, [bookId+kind]',
    })

    // v9: embeddings semánticos (segundo cerebro, Área C). Índice por bookId
    // y kind para consultas y borrado en cascada.
    this.version(9).stores({
      embeddings: 'id, bookId, kind, [bookId+kind]',
    })

    // v10: hilos de chat RAG persistidos (Área D). Índice por bookId (hilos
    // de un libro) y por scope (library vs book). updatedAt para orden.
    this.version(10).stores({
      chatThreads: 'id, bookId, scope, updatedAt',
    })

    // v11: tarjetas de repaso SRS (Área E). Índices por bookId (cascade/
    // filtro), highlightId (idempotencia), dueDate (vencidas) y color (filtro).
    this.version(11).stores({
      reviewCards: 'id, bookId, highlightId, dueDate, color',
    })

    // v12: authorId en highlights (atribución para lectura compartida, Área F).
    // No es un índice nuevo; bump de versión para señalar el cambio de schema
    // del campo. Los highlights antiguos quedan con authorId undefined/null.
    this.version(12).stores({
      highlights: 'id, bookId, createdAt, [bookId+createdAt]',
    })

    // v13: flag `favorite` en books para favoritos del usuario. Índice para
    // listar favoritos eficientemente. Los libros existentes se migran con
    // favorite=false (upgrade() abajo).
    this.version(13).stores({
      books: 'id, title, author, status, dateAdded, [status+dateAdded], contentHash, favorite',
    }).upgrade(async (tx) => {
      // Rellenar favorite=false en libros pre-existentes (undefined → false).
      await tx.table('books').toCollection().modify((book: Book) => {
        if (book.favorite === undefined) book.favorite = false
      })
    })

    // v14: cola de eventos para el pipeline de stats globales (panel admin).
    // Sin sync online → se acumulan aquí hasta que un flush los envía.
    this.version(14).stores({
      eventQueue: 'id, queuedAt, attempts',
    })

    // v15: flag `shareId` en books para libros llegados vía invitación
    // (lectura compartida, F1.1). Índice para listar "Compartidos conmigo"
    // eficientemente. Los libros existentes se migran con shareId=null.
    this.version(15).stores({
      books: 'id, title, author, status, dateAdded, [status+dateAdded], contentHash, favorite, shareId',
    }).upgrade(async (tx) => {
      // Rellenar shareId=null en libros pre-existentes (undefined → null).
      await tx.table('books').toCollection().modify((book: Book) => {
        if (book.shareId === undefined) book.shareId = null
      })
    })

    // v16: flag `publishedToFeed` en highlights para marcar los que se han
    // publicado al feed público /descubrir (F1.3). Bump de versión; no se
    // añade índice porque no filtramos por él a escala (sólo lectura puntual
    // por highlight en el panel de anotaciones).
    this.version(16).stores({}).upgrade(async (tx) => {
      await tx.table('highlights').toCollection().modify((h: Highlight) => {
        if (h.publishedToFeed === undefined) h.publishedToFeed = false
      })
    })

    // v17: store `highlightComments` para comentarios sobre highlights en
    // rooms compartidos (F2.1). Se sincroniza vía CRDT igual que highlights.
    this.version(17).stores({
      highlightComments: 'id, highlightId, [highlightId+createdAt], createdAt',
    })

    // v18: separar `fileBlob` y `coverBlob` de la tabla `books` a sus
    // propias tablas (`bookFiles`, `bookCovers`). Hasta v17 cada registro
    // `books` arrastraba el binario EPUB completo (varios MB) y la portada,
    // lo que hacía que `getAllBooks()` deserializara decenas de MB en cada
    // apertura de la biblioteca. Ahora la biblioteca solo lee metadata, y
    // los blobs se piden bajo demanda.
    //
    // PATRÓN CORRECTO: iterar los libros con un cursor explícito y hacer
    // `await` en cada `put` de las tablas nuevas. NO usar `collection.modify`
    // con operaciones async dentro del callback (Dexie no las espera y el
    // `delete` del campo puede ganar la carrera, perdiendo el blob). Ese bug
    // se detectó en producción: los libros pre-migración quedaban sin archivo.
    this.version(18).stores({
      bookFiles: 'id',
      bookCovers: 'id',
    }).upgrade(async (tx) => {
      const books = await tx.table('books').toArray()
      for (const book of books) {
        const fileBlob = (book as Record<string, unknown>).fileBlob as Blob | undefined
        const coverBlob = (book as Record<string, unknown>).coverBlob as Blob | undefined
        const id = book.id as string
        // Mover los blobs a sus tablas propias (con await, no fire-and-forget).
        if (fileBlob) {
          await tx.table('bookFiles').put({ id, blob: fileBlob })
        }
        if (coverBlob) {
          await tx.table('bookCovers').put({ id, blob: coverBlob })
        }
        // Conservar el tamaño del archivo como metadata para no tener que
        // cargar el blob solo para leer `.size` (sync cloud, ordering, etc.).
        if (book.fileSize === undefined) {
          book.fileSize = fileBlob?.size ?? 0
        }
        // Quitar los blobs inline del registro de books.
        delete (book as Record<string, unknown>).fileBlob
        delete (book as Record<string, unknown>).coverBlob
        await tx.table('books').put(book)
      }
    })

    // v19: caché de audio del modo audiolibro. Índice por bookId (borrado en
    // cascada al eliminar el libro) y por lastUsedAt (desalojo LRU ordenado).
    this.version(19).stores({
      audioCache: 'id, bookId, lastUsedAt',
    })

    // v20: favoritos cross-device. Un registro por libro marcado como
    // favorito, con `id` = bookId y `addedAt` para ordenar. La fuente de
    // verdad es el CRDT `favorites:${userId}`; esta tabla es solo caché
    // local para rellenar la UI sin esperar al sync al arrancar.
    this.version(20).stores({
      favorites: 'id, addedAt',
    })
  }
}

/** Instancia singleton compartida por toda la app. */
export const db = new EpubUnlimitedDB()

/** Clave bajo la que se guardan los ajustes globales en la tabla settings. */
export const SETTINGS_KEY = 'app-settings'
